import type { TopicContext, InterviewTopicState, TopicState, TopicUIState, TopicIdName } from '@/types/interview'

const STORAGE_KEY_PREFIX = 'topic_state_'

/**
 * Generate storage key for topic state
 */
export function getTopicStorageKey(interviewId: number | string, attemptNumber: number): string {
  return `${STORAGE_KEY_PREFIX}${interviewId}_${attemptNumber}`
}

/**
 * Initialize topic state from start API response
 */
export function initializeTopicState(
  topicIds: string[],
  topicContext: TopicContext | null,
  topicNames?: Map<string, string>
): InterviewTopicState {
  const topics: Record<string, TopicState> = {}
  
  // Build topicIdNames array with IDs and names
  const topicIdNames: TopicIdName[] = topicIds.map(id => ({
    id,
    name: topicNames?.get(id) || `Topic ${id.slice(0, 8)}...`
  }))
  
  // Initialize all topics as NOT_STARTED
  for (const topicId of topicIds) {
    topics[topicId] = {
      topicId,
      uiState: 'NOT_STARTED',
      phase: null,
      questionCount: 0
    }
  }
  
  // Determine the current topic ID
  let currentTopicId: string | null = null
  let phase: "BASELINE" | "DEEP_DIVE" | null = null
  
  if (topicContext && topicContext.current_topic_id) {
    // Use topic context if available
    currentTopicId = topicContext.current_topic_id
    phase = topicContext.phase
    
    const currentTopic = topics[currentTopicId]
    if (currentTopic) {
      // Set uiState based on phase: DEEP_DIVE -> DEEP_DIVE, BASELINE -> IN_PROGRESS
      currentTopic.uiState = topicContext.phase === 'DEEP_DIVE' ? 'DEEP_DIVE' : 'IN_PROGRESS'
      currentTopic.phase = topicContext.phase
    }
  } else if (topicIds.length > 0) {
    // If no topic context, mark the first topic as started
    currentTopicId = topicIds[0]
    phase = 'BASELINE' // Default to BASELINE phase
    
    const firstTopic = topics[currentTopicId]
    if (firstTopic) {
      firstTopic.uiState = 'IN_PROGRESS'
      firstTopic.phase = 'BASELINE'
    }
  }
  
  return {
    topicIdNames,
    topics,
    currentTopicId,
    phase
  }
}

/**
 * Update topic state from next question API response
 */
export function updateTopicState(
  currentState: InterviewTopicState,
  topicContext: TopicContext,
  topicName?: string
): InterviewTopicState {
  const newState = { ...currentState }
  newState.topics = { ...newState.topics }
  newState.topicIdNames = [...newState.topicIdNames]
  
  // Ensure current topic exists in topics map
  if (!newState.topics[topicContext.current_topic_id]) {
    // If topic doesn't exist, create it
    // Set uiState based on phase: DEEP_DIVE -> DEEP_DIVE, BASELINE -> IN_PROGRESS
    newState.topics[topicContext.current_topic_id] = {
      topicId: topicContext.current_topic_id,
      uiState: topicContext.phase === 'DEEP_DIVE' ? 'DEEP_DIVE' : 'IN_PROGRESS',
      phase: topicContext.phase,
      questionCount: 0
    }
    
    // Add to topicIdNames if not already present
    if (!newState.topicIdNames.some(t => t.id === topicContext.current_topic_id)) {
      newState.topicIdNames = [
        ...newState.topicIdNames,
        {
          id: topicContext.current_topic_id,
          name: topicName || `Topic ${topicContext.current_topic_id.slice(0, 8)}...`
        }
      ]
    }
  }

  if (topicContext.phase === 'DEEP_DIVE') {
    Object.values(newState.topics).forEach(topic => {
      if (topic.phase === 'BASELINE' && topic.uiState === 'IN_PROGRESS' && topic.topicId !== topicContext.current_topic_id) {
        topic.uiState = 'CLEARED'
      }
    })
  }
  
  // Update previous topic if it was completed
  if (topicContext.previous_topic_completed && topicContext.previous_topic_id) {
    const previousTopic = newState.topics[topicContext.previous_topic_id]
    if (previousTopic) {
      // Mark previous topic as CLEARED if it was in progress
      if (previousTopic.uiState === 'IN_PROGRESS') {
        previousTopic.uiState = 'CLEARED'
      }
    }
  }
  
  // Update current topic
  const currentTopic = newState.topics[topicContext.current_topic_id]
  if (currentTopic) {
    // Update phase first
    currentTopic.phase = topicContext.phase
    
    // If phase is DEEP_DIVE, always set uiState to DEEP_DIVE
    // This handles both transitions: IN_PROGRESS -> DEEP_DIVE and CLEARED -> DEEP_DIVE
    if (topicContext.phase === 'DEEP_DIVE') {
      currentTopic.uiState = 'DEEP_DIVE'
    } else if (currentTopic.uiState === 'NOT_STARTED') {
      // If topic was NOT_STARTED and phase is BASELINE, mark as IN_PROGRESS
      currentTopic.uiState = 'IN_PROGRESS'
    }
    // Note: If phase is BASELINE and topic is already IN_PROGRESS or CLEARED, keep the current state
    
    // Increment question count for current topic
    currentTopic.questionCount += 1
  }
  
  // Update previous topic if transitioning
  if (newState.currentTopicId && newState.currentTopicId !== topicContext.current_topic_id) {
    const oldCurrentTopic = newState.topics[newState.currentTopicId]
    if (oldCurrentTopic && oldCurrentTopic.uiState === 'IN_PROGRESS') {
      // Keep it as IN_PROGRESS if not explicitly cleared
      // The previous_topic_completed flag will handle clearing
    }
  }
  
  newState.currentTopicId = topicContext.current_topic_id
  newState.phase = topicContext.phase
  
  return newState
}

/**
 * Get UI state for a topic
 */
export function getTopicUIState(
  topicId: string,
  state: InterviewTopicState
): TopicUIState {
  const topic = state.topics[topicId]
  return topic?.uiState || 'NOT_STARTED'
}

/**
 * Save topic state to sessionStorage
 */
export function saveTopicState(state: InterviewTopicState, interviewId: number | string, attemptNumber: number): void {
  try {
    const key = getTopicStorageKey(interviewId, attemptNumber)
    sessionStorage.setItem(key, JSON.stringify(state))
  } catch (error) {
    // Error saving topic state
    console.error('Failed to save topic state:', error)
  }
}

/**
 * Load topic state from sessionStorage
 */
export function loadTopicState(interviewId: number | string, attemptNumber: number): InterviewTopicState | null {
  try {
    const key = getTopicStorageKey(interviewId, attemptNumber)
    const stored = sessionStorage.getItem(key)
    
    if (!stored) {
      return null
    }
    
    const parsed = JSON.parse(stored) as InterviewTopicState
    
    // Validate the loaded state has required fields
    if (!parsed || !parsed.topics || !parsed.topicIdNames || !Array.isArray(parsed.topicIdNames)) {
      console.warn('Invalid topic state in sessionStorage, ignoring')
      return null
    }
    
    return parsed
  } catch (error) {
    // Error loading topic state
    console.error('Failed to load topic state:', error)
    return null
  }
}

/**
 * Clear topic state from sessionStorage
 */
export function clearTopicState(interviewId: number | string, attemptNumber: number): void {
  try {
    const key = getTopicStorageKey(interviewId, attemptNumber)
    sessionStorage.removeItem(key)
  } catch (error) {
    // Error clearing topic state
    console.error('Failed to clear topic state:', error)
  }
}

